const unsplash = (id, w = 1600) =>
    `https://images.unsplash.com/${id}?q=80&w=${w}&auto=format&fit=crop`;

export const INSIGHTS = [
    {
        slug: "cost-to-build-a-house-in-south-africa",
        title: "How Much Does It Cost to Build a House in South Africa?",
        excerpt:
            "The honest answer: it depends on your design, your site, your finishes and your region. Here is how the pieces fit together — and how to turn 'it depends' into a number you can plan around.",
        readTime: "5 MIN READ",
        date: "NOVEMBER 2026",
        image: unsplash("photo-1657346088167-b982455bf29a"),
        alt: "Contemporary double-storey South African home at dusk",
        sections: [
            {
                h: "There is no single number — there is a structure",
                p: [
                    "Ask what it costs to build a house in South Africa and you will usually get a rate per square metre. The problem is that the same house can be quoted very differently depending on its design complexity, the slope of the site, the finishes chosen, the region you are building in and when you ask.",
                    "Instead of hunting for a magic rate, it is more useful to understand the structure of a building budget: preliminaries and site costs, the structural shell (foundations, masonry, roofing), windows and doors, internal finishes, electrical and plumbing, and then external works. Each behaves differently as you change your design.",
                ],
            },
            {
                h: "Where the money actually moves",
                p: [
                    "Internal finishes (kitchens, tiling, joinery, paint) and the structural shell are typically the two largest families of spending on a residential build. Finishes are also where homeowners have the most choice — and therefore the most room to steer the budget in either direction.",
                    "Design choices cascade. Changing the exterior finish from plaster to face brick, or specifying larger glazing, moves quantities — and quantities move cost. This is exactly the connection BuildEase's design-to-cost intelligence is being built to make visible before you commit.",
                ],
            },
            {
                h: "An illustrative example",
                p: [
                    "For a sense of proportion only: an illustrative 286 m² double-storey home in Durban might carry an estimated build cost in the low millions of rand, before VAT, professional fees and site-specific allowances. That figure is an illustration of scale, not a market price — your project needs its own estimate built from your plans and current local pricing.",
                ],
            },
            {
                h: "How BuildEase helps",
                p: [
                    "BuildEase organises the construction information available for your project into structured quantities and cost estimates, with each figure labelled Verified, Estimated or Assumed, and its assumptions stated. You can explore what is driving the number instead of trusting it blindly.",
                    "Estimates depend on document quality and available information — they are decision-support, not a quantity surveyor's formal estimate or a contractor's binding quotation.",
                ],
            },
        ],
        takeaway:
            "Understand the structure of your building budget first. BuildEase's BOQ & Cost Intelligence is being built to turn your plans into that structure — with the assumptions shown.",
    },
    {
        slug: "what-is-a-bill-of-quantities",
        title: "What Is a Bill of Quantities (BOQ)?",
        excerpt:
            "The document that turns a drawing into a price. What a BOQ contains, why contractors lean on it, and how homeowners can read one without a QS qualification.",
        readTime: "4 MIN READ",
        date: "NOVEMBER 2026",
        image: unsplash("photo-1721244654394-36a7bc2da288"),
        alt: "Architectural elevation drawing of a residence",
        sections: [
            {
                h: "The bridge between design and price",
                p: [
                    "A Bill of Quantities (BOQ) is a structured list of the work and materials a building project needs, measured from the drawings: cubic metres of excavation, square metres of brickwork, numbers of doors and windows, and so on. Pricing those measured quantities is how a design becomes a cost.",
                    "In South African residential construction, a detailed BOQ is typically prepared by a quantity surveyor. Contractors use it (or build their own version) to price work consistently — which is also why two quotes for the same house can differ simply because they measure different things.",
                ],
            },
            {
                h: "How to read one without a QS qualification",
                p: [
                    "You do not need to audit a BOQ to benefit from it. Three habits go a long way: check that the sections cover the whole house (foundations to roof to finishes), look for quantities that seem out of proportion to your plans, and pay attention to what is described as a provisional sum — an allowance for work that cannot yet be measured accurately, like underground conditions.",
                    "Also watch the exclusions. What a BOQ deliberately leaves out (VAT, professional fees, site clearance, landscaping) is as important as what it includes.",
                ],
            },
            {
                h: "How BuildEase helps",
                p: [
                    "BuildEase is being developed to organise the construction information available for your project into structured quantities and BOQ-style cost breakdowns — with every figure labelled Verified, Estimated or Assumed, and the assumptions and exclusions stated alongside.",
                    "It supports your understanding of the document; it is not a substitute for a quantity surveyor's formal BOQ where one is required.",
                ],
            },
        ],
        takeaway:
            "A BOQ is just your house, measured. BuildEase's BOQ & Cost Intelligence is being built to give you that measurement in readable form.",
    },
    {
        slug: "evaluate-a-building-contractors-quote",
        title: "How to Evaluate a Building Contractor's Quote",
        excerpt:
            "A quote is a proposal, not a verdict. A practical, neutral checklist for comparing contractor quotations — scope, specifications, exclusions and the questions worth asking.",
        readTime: "5 MIN READ",
        date: "NOVEMBER 2026",
        image: unsplash("photo-1450101499163-c8848c66ca85"),
        alt: "Documents being compared and reviewed at a desk",
        sections: [
            {
                h: "Start with scope, not price",
                p: [
                    "Two quotations rarely describe the same work. One may include site preparation and boundary walls; the other may assume they are done. Before comparing rands, compare scope: what is included, what is excluded, and what is described as a provisional sum. A cheaper quote with more exclusions is not a cheaper build.",
                ],
            },
            {
                h: "Compare specifications line by line",
                p: [
                    "Check that the materials and finishes named are the ones you expect: the brick type, the window material, the sanitaryware brand ranges, the roof covering. A quote that says 'standard' where you expected a specific product is an item requiring clarification — not necessarily a red flag, but definitely a question.",
                    "This is also where pricing differences usually live. When one quote is higher, look for the line item that explains it: a different specification, a different measurement, or an allowance the other quote left out.",
                ],
            },
            {
                h: "Write down what is unclear — then ask",
                p: [
                    "Every quote review should end with a short list of questions: Which items are provisional? What happens if quantities change? Is VAT included? When does the price expire? A contractor who answers clearly is giving you exactly the information you need.",
                    "A neutral review process protects both sides. The goal is not to accuse — it is to make sure the price you accept is the price you both understand.",
                ],
            },
            {
                h: "How BuildEase helps",
                p: [
                    "BuildEase's Quote Analyser breaks quotations into line items and shows neutral assessments — items within an observed range, prices that may require review, and specifications that need clarification — so you can ask better questions before you sign. Assessments depend on the information supplied and are not a judgement of any contractor.",
                ],
            },
        ],
        takeaway:
            "Compare scope, specifications and exclusions before price. BuildEase's Quote Analyser is built to structure exactly that comparison.",
    },
];

export const COMING_SOON = [
    "Understanding Architectural Plans Before Building",
    "What Is Included in a Residential Construction Estimate?",
    "Common Causes of Construction Budget Overruns",
    "Understanding NHBRC Requirements When Building a Home",
    "SANS 10400 Explained for Homeowners",
    "How to Choose Building Materials",
    "What to Consider Before Building a House in KwaZulu-Natal",
];
